import React, { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import axios from 'axios';
import { Box, Typography, CircularProgress } from '@mui/material';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const API_BASE = 'https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com';

function normalizeId(value) {
  if (value === undefined || value === null) return '';
  const raw = String(value).trim();
  if (!raw) return '';

  if (raw.includes('/')) {
    return raw.split('/').pop();
  }

  return raw;
}

function getDateString(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
}

function getQuantityValue(item) {
  const candidates = [
    item?.quantity,
    item?.sold,
    item?.soldQuantity,
    item?.quantitySold,
    item?.unitsSold,
    item?.dailySold,
    item?.sales,
    item?.count,
    item?.totalSold,
  ];

  const found = candidates.find((v) => v !== undefined && v !== null && v !== '');
  return Number(found || 0);
}

function SalesGraph({ productId, rangeStart }) {
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchSales = async () => {
      setLoading(true);

      try {
        const response = await axios.get(`${API_BASE}/api/sales-since-august-11`, {
          params: { productId },
        });

        console.log('SalesGraph raw API response:', response.data);
        console.log('SalesGraph productId:', productId);

        const normalizedTargetId = normalizeId(productId);
        const salesMap = new Map();

        (response.data || []).forEach((entry) => {
          // Case 1: backend returns full orders with line_items
          if (Array.isArray(entry?.line_items)) {
            const orderDate =
              getDateString(entry.processed_at) ||
              getDateString(entry.created_at) ||
              getDateString(entry.updated_at);

            if (!orderDate) return;

            entry.line_items.forEach((item) => {
              const candidateIds = [
                item?.product_id,
                item?.productId,
                item?.shopify_product_id,
                item?.shopifyId,
                item?.id,
                item?.admin_graphql_api_id,
              ]
                .map(normalizeId)
                .filter(Boolean);

              const isMatch = candidateIds.includes(normalizedTargetId);

              if (isMatch) {
                const qty = Number(item?.quantity || 0);
                salesMap.set(orderDate, (salesMap.get(orderDate) || 0) + qty);
              }
            });

            return;
          }

          // Case 2: backend returns aggregated rows already
          const entryDate =
            getDateString(entry.date) ||
            getDateString(entry.day) ||
            getDateString(entry.processed_at) ||
            getDateString(entry.created_at) ||
            getDateString(entry.updated_at);

          const candidateIds = [
            entry?.product_id,
            entry?.productId,
            entry?.shopify_product_id,
            entry?.shopifyId,
            entry?.id,
            entry?.admin_graphql_api_id,
          ]
            .map(normalizeId)
            .filter(Boolean);

          const hasNoProductField = candidateIds.length === 0;
          const isMatch = hasNoProductField || candidateIds.includes(normalizedTargetId);

          if (entryDate && isMatch) {
            const qty = getQuantityValue(entry);
            salesMap.set(entryDate, (salesMap.get(entryDate) || 0) + qty);
          }
        });

        console.log('SalesGraph salesMap:', Array.from(salesMap.entries()));

        const startDate = new Date('2024-08-11');
        const endDate = new Date();
        const dateArray = [];
        const currentDate = new Date(startDate);

        while (currentDate <= endDate) {
          const dateStr = currentDate.toISOString().split('T')[0];
          dateArray.push(dateStr);
          currentDate.setDate(currentDate.getDate() + 1);
        }

        const finalSalesData = dateArray.map((date) => ({
          date,
          quantity: salesMap.get(date) || 0,
        }));

        if (isMounted) {
          setSalesData(finalSalesData);
        }
      } catch (error) {
        console.error('Error fetching sales data:', error);
        if (isMounted) {
          setSalesData([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSales();

    return () => {
      isMounted = false;
    };
  }, [productId]);

  const displayData = useMemo(() => {
    const start = rangeStart || new Date(0);
    return salesData.filter((entry) => new Date(entry.date) >= start);
  }, [salesData, rangeStart]);

  const labels = useMemo(() => displayData.map((entry) => entry.date), [displayData]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Quantity Sold',
          data: displayData.map((entry) => entry.quantity),
          fill: false,
          borderColor: '#f59e0b',
          tension: 0.15,
          pointHitRadius: 6,
          pointHoverRadius: 5,
          pointBackgroundColor: '#f59e0b',
          borderWidth: 2,
        },
      ],
    }),
    [labels, displayData]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            font: { family: 'Inter', size: 12 },
            usePointStyle: true,
            pointStyleWidth: 8,
          },
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          titleFont: { family: 'Inter' },
          bodyFont: { family: 'Inter' },
          callbacks: {
            label: (context) => `Quantity Sold: ${context.raw}`,
            title: (context) => displayData[context[0].dataIndex]?.date || 'Date: N/A',
          },
        },
      },
      interaction: { mode: 'index', intersect: false },
      scales: {
        x: {
          ticks: { font: { family: 'Inter', size: 10 } },
          grid: { color: 'rgba(0,0,0,0.04)' },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Quantity Sold',
            font: { family: 'Inter', size: 11 },
          },
          grid: { color: 'rgba(0,0,0,0.04)' },
          ticks: {
            font: { family: 'Inter', size: 10 },
            precision: 0,
          },
        },
      },
    }),
    [displayData]
  );

  return (
    <Box sx={{ width: '100%', height: 400 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress size={28} thickness={3} sx={{ color: '#f59e0b' }} />
        </Box>
      ) : (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Sales
          </Typography>
          <Line data={data} options={options} />
        </>
      )}
    </Box>
  );
}

export default SalesGraph;